import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cart from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/cart.tsx";
import FileList from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-list.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import { DataSourceProvider } from "common";
import {
  BucketObject,
  FileBrowserBadge,
  formatBucketObjects,
  globStringToRegex,
  keyOf,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { AdditionalArea } from "@via/pages/data/data-sources/cloud-data-sources/amazon-s3/amazon-folder-browser.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { formatBasespaceObject } from "@via/pages/data/data-sources/token-based-data-sources/basespace/basespace-folder-browser.tsx";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";

interface FileBrowserProps {
  dataSourceProvider: DataSourceProvider;
  objects: BucketObject[];
  onObjectClick: any;
  getExtraFolderObjects: any;
  onContinue: any;
  isGetFolderListLoading: boolean;
  isGetExtraFolderListLoading: boolean;
  filterValue?: string;
  onAdditionalAreaButtonClick: (item: AdditionalArea) => void;
  additionalArea: AdditionalArea | null;
  setAdditionalArea: (item: AdditionalArea | null) => void;
  setShowFileUpload?: (showFileUpload: boolean) => void;
  cart?: BucketObject[];
  setCart?: (cart: BucketObject[]) => void;
}

export function filterFilesByGlob(
  files: BucketObject[],
  pattern?: string,
  allowFolders = false
): BucketObject[] {
  let result = allowFolders
    ? files
    : files.filter(file => file.type !== "folder");

  if (pattern && pattern.trim().length > 0) {
    const trimmed = pattern.trim();

    // If pattern includes glob-like characters, use glob-based matching
    if (/[?*]/.test(trimmed)) {
      const regex = globStringToRegex(trimmed);
      result = result.filter(file => regex.test(file.displayName ?? ""));
    } else {
      // Otherwise, use simple case-insensitive substring match
      const lower = trimmed.toLowerCase();
      result = result.filter(file =>
        (file.displayName ?? "").toLowerCase().includes(lower)
      );
    }
  }

  return result;
}

const shouldDisableFolderSelection = (
  dataSourceProvider: DataSourceProvider
) => {
  // Remote servers list one folder at a time (data-1-2); selecting a whole folder needs a recursive listing, not built yet.
  return (
    dataSourceProvider === DataSourceProvider.URL ||
    dataSourceProvider === DataSourceProvider.RemoteStorage
  );
};

export const FileBrowser: React.FC<FileBrowserProps> = ({
  dataSourceProvider,
  objects,
  onObjectClick,
  getExtraFolderObjects,
  onContinue,
  isGetFolderListLoading,
  isGetExtraFolderListLoading,
  filterValue,
  onAdditionalAreaButtonClick,
  additionalArea,
  setAdditionalArea,
  setShowFileUpload,
  cart: cartProp,
  setCart: setCartProp,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const filteredObjects = useMemo(() => {
    const term = searchInput.trim();
    if (!term) return objects;
    return filterFilesByGlob(objects, term, true);
  }, [searchInput, objects]);

  const [hasParentRowChecked, setHasParentRowChecked] = useState(false);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  // Use prop-based cart if provided, otherwise fall back to local state for backward compatibility
  const [localCart, setLocalCart] = useState<BucketObject[]>([]);
  const cart = cartProp ?? localCart;

  // Wrapper function to handle both prop-based and local state setters
  const setCart = useCallback(
    (updater: BucketObject[] | ((prev: BucketObject[]) => BucketObject[])) => {
      if (setCartProp) {
        // handle functional updates manually for prop-based setter
        if (typeof updater === "function") {
          const newValue = updater(cartProp ?? []);
          setCartProp(newValue);
        } else {
          setCartProp(updater);
        }
      } else {
        setLocalCart(updater);
      }
    },
    [setCartProp, cartProp, setLocalCart]
  );

  /**
   * Determines whether the "Recursive Filter" option should be shown.
   * Show Recursive Filter when user selects folder(s)
   * Logic:
   * - For BaseSpace data sources: show it if at least one selected item is a folder.
   *   A folder is identified when the item does *not* end with "fastq.gz".
   * - For other data sources: show it if at least one selected item ends with a "/",
   *   which conventionally indicates a folder path.
   */
  const showRecursiveFilter =
    dataSourceProvider === DataSourceProvider.BaseSpace
      ? Array.from(selectedKeys).some(item => !item.endsWith("fastq.gz"))
      : Array.from(selectedKeys).some(item => item.endsWith("/"));

  useEffect(() => {
    if (!showRecursiveFilter) {
      setAdditionalArea(null);
    }
  }, [showRecursiveFilter]);

  const objectsMemo = useMemo(
    () => filteredObjects.filter(obj => obj.id !== ".."),
    [filteredObjects]
  );

  useEffect(() => {
    clearSelection();
    setSearchInput("");
  }, [objects]);

  // Add selected files to cart, ignoring duplicates
  const handleAddSelectedToCart = async () => {
    if (selectedKeys.size === 0) return;

    const selectedMap = new Set(selectedKeys);
    const existing = [...cart];
    const newCandidates: BucketObject[] = [];

    for (const f of filteredObjects) {
      const k = keyOf(f);
      if (selectedMap.has(k) && !existing.some(x => keyOf(x) === k)) {
        newCandidates.push(f);
      }
    }

    /**
     * Handle folder selection
     */
    for (const item of newCandidates) {
      if (item.type === "folder") {
        const extraFolderObjects = await getExtraFolderObjects(item);

        const formattedExtraObjects =
          dataSourceProvider === DataSourceProvider.BaseSpace
            ? formatBasespaceObject(extraFolderObjects)
            : formatBucketObjects(extraFolderObjects, dataSourceProvider);

        // Only add brand-new ones
        const extraNewItems = formattedExtraObjects.filter(
          extraItem =>
            !existing.some(x => keyOf(x) === keyOf(extraItem)) &&
            !newCandidates.some(x => keyOf(x) === keyOf(extraItem))
        );

        newCandidates.push(...extraNewItems);
      }
    }

    // apply regex/glob filtering **only to new items**
    const filteredNew = filterFilesByGlob(newCandidates, filterValue);

    // merge old + new
    setCart([...existing, ...filteredNew]);
  };

  const toggleSelect = (item: BucketObject) => {
    const k = keyOf(item);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const clickSelected = (item: BucketObject) => {
    if (item.type === "folder") {
      onObjectClick(item);
    }
  };

  const selectAllFiles = () => {
    setSelectedKeys(new Set(objectsMemo.map(keyOf)));
  };

  const clearSelection = () => {
    setSelectedKeys(new Set());
    setHasParentRowChecked(false);
  };

  const removeFromCart = (key: string) => {
    setCart(prev => prev.filter(x => keyOf(x) !== key));
  };

  const clearCart = () => setCart([]);

  const handleContinueClick = () => {
    onContinue(cart);
  };

  return (
    <div className="py-4">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_480px] md:h-[calc(100vh-220px)]">
        <div className="flex flex-col gap-4 min-h-0 overflow-hidden">
          <div className="flex items-center justify-between shrink-0">
            <div className="text-lg font-semibold text-gray-900">Browse</div>
            <div className="flex items-center gap-2">
              {setShowFileUpload && (
                <button
                  data-testid="folderBrowserUploadButton"
                  onClick={() => setShowFileUpload(true)}
                  disabled={false}
                  className="inline-flex justify-center rounded-md border border-indigo-600 bg-white px-3 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Upload File(s)
                </button>
              )}
              {showRecursiveFilter && (
                <button
                  data-testid="fileFilterButton"
                  onClick={() =>
                    onAdditionalAreaButtonClick(AdditionalArea.Filters)
                  }
                  className={classNames(
                    "flex items-center space-x-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 transition",
                    additionalArea === AdditionalArea.Filters
                      ? "bg-gray-200"
                      : ""
                  )}>
                  <FunnelIcon className="h-5 w-5 text-gray-700" />
                  <span>Recursive Filter</span>
                </button>
              )}
              <button
                data-testid="addToCartButton"
                onClick={handleAddSelectedToCart}
                disabled={
                  selectedKeys.size === 0 || isGetExtraFolderListLoading
                }
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
                <Spinner loading={isGetExtraFolderListLoading}>
                  Add Selected{" "}
                  <FileBrowserBadge count={selectedKeys.size} />{" "}
                </Spinner>
              </button>
            </div>
          </div>

          {isGetFolderListLoading ? (
            <SkeletonLoader count={1} />
          ) : (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="mt-2 shrink-0">
                <div className="relative flex items-center">
                  <MagnifyingGlassIcon className="pointer-events-none absolute mb-2 left-3 h-4 w-4 text-gray-700" />
                  <input
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    type="text"
                    placeholder="Type to filter... e.g., *.fastq.gz, sample1"
                    className="mb-2 w-full rounded-md border-0 py-2 pl-9 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-hidden">
                <FileList
                  dataSourceProvider={dataSourceProvider}
                  items={filteredObjects}
                  selectedKeys={selectedKeys}
                  onToggleSelect={toggleSelect}
                  onClickSelected={clickSelected}
                  onSelectAll={selectAllFiles}
                  onClearSelection={clearSelection}
                  isGetFolderListLoading={isGetFolderListLoading}
                  disableFolderSelection={shouldDisableFolderSelection(
                    dataSourceProvider
                  )}
                  hasParentRowChecked={hasParentRowChecked}
                  setHasParentRowChecked={setHasParentRowChecked}
                  variant="dataPage"
                />
              </div>

              <div className="mt-2 ml-4 text-sm text-gray-400">
                Tip: Select files, then click Add Selected to include them in
                your dataset.
              </div>
            </div>
          )}
        </div>

        <div className="min-h-0 overflow-hidden">
          <Cart
            items={cart}
            onRemove={removeFromCart}
            onClear={clearCart}
            onContinue={handleContinueClick}
            variant="dataPage"
          />
        </div>
      </div>
    </div>
  );
};
